import { StepsProps } from "@amzn/awsui-components-react/polaris/steps/interfaces";
import { useHealthState } from "../contexes/HealthStateContext";
import { DlqStatus } from "../components/dlq/DlqStatusIndicator";

export interface FlowSteps {
  accountingFlow: StepsProps.Step[];
  documentFlow: StepsProps.Step[];
  dlqCheck: StepsProps.Step;
  hideAccountingFlow: boolean;
  hideDocumentFlow: boolean;
}

// Compute step statuses for overall health UI
export function useHealthStatus(dlqStatus: DlqStatus) {
  const {
    eventsReceived: globalEvents,
    cbeStatus: { produced: globalProduced, published: globalPublished },
    accountingFlow,
    documentFlow,
    hasOnlyNoOpCbes: globalNoOp = false,
  } = useHealthState();

  // 1) Map DLQ status
  function mapDlqStatus(s: DlqStatus): StepsProps.Status {
    switch (s) {
      case "notInDlq":
        return "success";
      case "inDlq":
        return "error";
      case "loading":
        return "loading";
      default:
        return "error";
    }
  }

  const dlqStep: StepsProps.Step = {
    header: "DLQ Check",
    status: mapDlqStatus(dlqStatus),
  };

  // 2) Build the 3-step status
  function buildStepsForFlow({
    eventsReceived,
    cbesProduced,
    cbesPublished,
    hasOnlyNoOp = false,
  }: {
    eventsReceived: boolean;
    cbesProduced: boolean;
    cbesPublished: boolean;
    hasOnlyNoOp?: boolean;
  }): StepsProps.Step[] {
    // a) Events Received Step
    const eventsStep: StepsProps.Step = {
      header: "Events Received",
      status: eventsReceived ? "success" : "error",
    };

    // b) CBEs Produced: not-started if none, success if produced, in-progress only for NoOp
    let producedStatus: StepsProps.Status;
    if (hasOnlyNoOp) {
      producedStatus = "in-progress";
    } else if (cbesProduced) {
      producedStatus = "success";
    } else {
      producedStatus = "not-started" as StepsProps.Status;
    }
    const producedStep: StepsProps.Step = {
      header: "CBEs Produced",
      status: producedStatus,
    };

    // c) CBEs Published Step
    let publishedStepStatus: StepsProps.Status = hasOnlyNoOp
      ? "in-progress"
      : ("not-started" as StepsProps.Status);
    if (!hasOnlyNoOp && cbesProduced) {
      publishedStepStatus = cbesPublished ? "success" : "in-progress";
    }
    const publishedStep: StepsProps.Step = {
      header: "CBEs Published",
      status: publishedStepStatus,
    };

    return [eventsStep, producedStep, publishedStep];
  }

  const showAcct = !accountingFlow.hidden;
  const showDoc = !documentFlow.hidden;

  // 3) Decide which sources to use

  // Both flows visible (default)
  let healthOpts = {
    eventsReceived: globalEvents,
    cbesProduced: globalProduced,
    cbesPublished: globalPublished,
    hasOnlyNoOp: globalNoOp,
  };

  if (showAcct && !showDoc) {
    // only accounting visible
    healthOpts = {
      eventsReceived: accountingFlow.eventsReceived,
      cbesProduced: accountingFlow.cbesProduced,
      cbesPublished: accountingFlow.cbesPublished,
      hasOnlyNoOp: accountingFlow.hasOnlyNoOpCbes ?? false,
    };
  } else if (!showAcct && showDoc) {
    // only document visible
    healthOpts = {
      eventsReceived: documentFlow.eventsReceived,
      cbesProduced: documentFlow.cbesProduced,
      cbesPublished: documentFlow.cbesPublished,
      hasOnlyNoOp: false, // no-op CBEs never affect document flow
    };
  }

  // 4) Assemble healthSteps (+ DLQ)
  const healthSteps: StepsProps.Step[] = [
    ...buildStepsForFlow(healthOpts),
    dlqStep,
  ];

  // 5) Per-flow detail
  const flowSteps: FlowSteps = {
    accountingFlow: buildStepsForFlow({
      eventsReceived: accountingFlow.eventsReceived,
      cbesProduced: accountingFlow.cbesProduced,
      cbesPublished: accountingFlow.cbesPublished,
      hasOnlyNoOp: accountingFlow.hasOnlyNoOpCbes ?? false,
    }),
    documentFlow: buildStepsForFlow({
      eventsReceived: documentFlow.eventsReceived,
      cbesProduced: documentFlow.cbesProduced,
      cbesPublished: documentFlow.cbesPublished,
      hasOnlyNoOp: false,
    }),
    dlqCheck: dlqStep,
    hideAccountingFlow: !showAcct,
    hideDocumentFlow: !showDoc,
  };

  return { healthSteps, flowSteps };
}
